import React, { useState, useEffect } from 'react';
import {
    Card, Table, Button, Modal, Form, Input, InputNumber,
    Switch, Select, Space, Tag, Tabs, message, Popconfirm,
    Row, Col, Divider, Tooltip
} from 'antd';
import {
    PlusOutlined, EditOutlined, SaveOutlined,
    CheckCircleOutlined, StopOutlined, HistoryOutlined,
    SettingOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import execute from '../../hooks/execute';
import { protectedResources } from '../../config/msalConfig';

const SectionCard = styled(Card)`
    margin-bottom: 16px;
    .ant-card-head-title {
        font-weight: 600;
    }
`;

const PlanCard = styled(Card)`
    cursor: pointer;
    transition: all 0.3s;
    border: 2px solid ${props => props.$selected ? '#F16622' : '#e8e8e8'};

    &:hover {
        border-color: #F16622;
        box-shadow: 0 2px 8px rgba(241, 102, 34, 0.15);
    }

    .plan-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
    }

    .plan-name {
        font-size: 18px;
        font-weight: 600;
        color: #002C57;
    }

    .plan-tokens {
        color: #666;
        font-size: 13px;
        margin-top: 8px;
    }
`;

const FeatureRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
        border-bottom: none;
    }

    .feature-name {
        font-weight: 500;
    }

    .feature-area {
        color: #999;
        font-size: 12px;
    }
`;

const formatTokens = (tokens) => {
    if (tokens >= 1000000) {
        return `${(tokens / 1000000).toFixed(1)}M`;
    } else if (tokens >= 1000) {
        return `${(tokens / 1000).toFixed(0)}K`;
    }
    return tokens;
};

const PlanConfiguration = ({ plans, features, policy, onRefresh, onPolicyRefresh, loading }) => {
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [planDetails, setPlanDetails] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isPolicyModalVisible, setIsPolicyModalVisible] = useState(false);
    const [isPricingModalVisible, setIsPricingModalVisible] = useState(false);
    const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
    const [versionHistory, setVersionHistory] = useState([]);
    const [modalMode, setModalMode] = useState('create');
    const [saving, setSaving] = useState(false);
    const [updatingFeatureId, setUpdatingFeatureId] = useState(null);
    const [form] = Form.useForm();
    const [policyForm] = Form.useForm();
    const [pricingForm] = Form.useForm();

    useEffect(() => {
        if (plans?.length > 0 && !selectedPlan) {
            setSelectedPlan(plans[0]);
        }
    }, [plans]);

    useEffect(() => {
        if (selectedPlan) {
            fetchPlanDetails(selectedPlan.PlanId);
        }
    }, [selectedPlan]);

    useEffect(() => {
        if (selectedPlan?.PlanId && features?.length > 0) {
            fetchPlanDetails(selectedPlan.PlanId);
        }
    }, [features]);

    useEffect(() => {
        if (policy) {
            policyForm.setFieldsValue({
                trialDaysDefault: policy.TrialDaysDefault,
                trialDaysMin: policy.TrialDaysMin,
                trialDaysMax: policy.TrialDaysMax,
                warnThresholdPct1: policy.WarnThresholdPct1,
                warnThresholdPct2: policy.WarnThresholdPct2,
                blockThresholdPct: policy.BlockThresholdPct,
                blockAtThreshold: policy.BlockAtThreshold,
                emailNotificationsEnabled: policy.EmailNotificationsEnabled,
                teamsNotificationsEnabled: policy.TeamsNotificationsEnabled
            });
        }
    }, [policy, policyForm]);

    const normalizePlanDetails = (rawPlanDetails) => {
        const mappedFeatures = rawPlanDetails.features || [];
        const featureCatalog = features?.length ? features : mappedFeatures;
        const mappedFeatureById = new Map(mappedFeatures.map((feature) => [feature.FeatureId, feature]));

        const normalizedFeatures = featureCatalog
            .map((feature, idx) => {
                const mapped = mappedFeatureById.get(feature.FeatureId);
                return {
                    ...feature,
                    ...(mapped || {}),
                    FeatureId: feature.FeatureId ?? mapped?.FeatureId,
                    FeatureName: feature.FeatureName ?? mapped?.FeatureName,
                    Area: feature.Area ?? mapped?.Area,
                    DisplayOrder: feature.DisplayOrder ?? mapped?.DisplayOrder ?? idx,
                    IsEnabled: mapped ? !!mapped.IsEnabled : false
                };
            })
            .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));

        const normalizedPricingTiers = [...(rawPlanDetails.pricingTiers || [])]
            .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));

        return {
            ...rawPlanDetails,
            features: normalizedFeatures,
            pricingTiers: normalizedPricingTiers
        };
    };

    const fetchPlanDetails = async (planId) => {
        try {
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/ai-subscription/plans/${planId}`
            );
            if (response.success) {
                setPlanDetails(normalizePlanDetails(response.data));
            }
        } catch (error) {
            message.error('Failed to fetch plan details');
        }
    };

    const handleEditPricingTiers = () => {
        if (!planDetails) return;

        const initialTiers = (planDetails.pricingTiers || []).map((tier, idx) => ({
            tierName: tier.TierName,
            minUsers: tier.MinUsers,
            maxUsers: tier.MaxUsers,
            pricePerUserMonthly: tier.PricePerUserMonthly,
            pricePerUserAnnual: tier.PricePerUserAnnual,
            basePrice: tier.BasePrice,
            displayOrder: tier.DisplayOrder || idx + 1
        }));

        pricingForm.setFieldsValue({
            tiers: initialTiers.length > 0
                ? initialTiers
                : [{ tierName: 'Small', minUsers: 1, maxUsers: null, pricePerUserMonthly: 0, displayOrder: 1 }]
        });

        setIsPricingModalVisible(true);
    };

    const handleSavePricingTiers = async () => {
        if (!selectedPlan?.PlanId) return;

        try {
            const values = await pricingForm.validateFields();
            const tiers = (values.tiers || []).map((tier, idx) => ({
                tierName: tier.tierName?.trim(),
                minUsers: Number(tier.minUsers),
                maxUsers: tier.maxUsers === undefined || tier.maxUsers === null || tier.maxUsers === '' ? null : Number(tier.maxUsers),
                pricePerUserMonthly: Number(tier.pricePerUserMonthly),
                pricePerUserAnnual: tier.pricePerUserAnnual === undefined || tier.pricePerUserAnnual === null || tier.pricePerUserAnnual === '' ? null : Number(tier.pricePerUserAnnual),
                basePrice: tier.basePrice === undefined || tier.basePrice === null || tier.basePrice === '' ? null : Number(tier.basePrice),
                displayOrder: idx + 1
            }));

            if (tiers.length === 0) {
                message.error('At least one pricing tier is required');
                return;
            }

            const hasInvalidRange = tiers.some((tier) => tier.maxUsers !== null && tier.maxUsers < tier.minUsers);
            if (hasInvalidRange) {
                message.error('Max Users must be greater than or equal to Min Users');
                return;
            }

            setSaving(true);
            const response = await execute(
                'PUT',
                `${protectedResources.api.baseEndPoint}/ai-subscription/pricing/${selectedPlan.PlanId}`,
                { tiers }
            );

            if (response.success) {
                message.success('Pricing tiers updated successfully');
                setIsPricingModalVisible(false);
                fetchPlanDetails(selectedPlan.PlanId);
            } else {
                message.error(response.message || 'Failed to update pricing tiers');
            }
        } catch (error) {
            message.error('Failed to update pricing tiers');
        } finally {
            setSaving(false);
        }
    };

    const fetchVersionHistory = async (planId) => {
        try {
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/ai-subscription/plans/${planId}/version-history`
            );
            if (response.success) {
                setVersionHistory(response.data);
                setIsHistoryModalVisible(true);
            }
        } catch (error) {
            message.error('Failed to fetch version history');
        }
    };

    const handleCreatePlan = () => {
        setModalMode('create');
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEditPlan = (plan) => {
        setModalMode('edit');
        form.setFieldsValue({
            planCode: plan.PlanCode,
            planName: plan.PlanName,
            description: plan.Description,
            tokenLimitMonthly: plan.TokenLimitMonthly,
            defaultUserTokenLimitMonthly: plan.DefaultUserTokenLimitMonthly
        });
        setIsModalVisible(true);
    };

    const handleSavePlan = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);

            const endpoint = modalMode === 'create'
                ? `${protectedResources.api.baseEndPoint}/ai-subscription/plans`
                : `${protectedResources.api.baseEndPoint}/ai-subscription/plans/${selectedPlan.PlanId}`;

            const method = modalMode === 'create' ? 'POST' : 'PUT';

            const response = await execute(method, endpoint, values);

            if (response.success) {
                message.success(`Plan ${modalMode === 'create' ? 'created' : 'updated'} successfully`);
                setIsModalVisible(false);
                onRefresh();
                if (modalMode === 'edit') {
                    fetchPlanDetails(selectedPlan.PlanId);
                }
            } else {
                message.error(response.message || 'Failed to save plan');
            }
        } catch (error) {
            message.error('Failed to save plan');
        } finally {
            setSaving(false);
        }
    };

    const handlePublishPlan = async (planId) => {
        try {
            const response = await execute(
                'POST',
                `${protectedResources.api.baseEndPoint}/ai-subscription/plans/${planId}/publish`,
                { reason: 'Published from Admin Console' }
            );
            if (response.success) {
                message.success('Plan published successfully');
                onRefresh();
                fetchPlanDetails(planId);
            } else {
                message.error(response.message || 'Failed to publish plan');
            }
        } catch (error) {
            message.error('Failed to publish plan');
        }
    };

    const handleArchivePlan = async (planId) => {
        try {
            const response = await execute(
                'POST',
                `${protectedResources.api.baseEndPoint}/ai-subscription/plans/${planId}/archive`,
                { reason: 'Archived from Admin Console' }
            );
            if (response.success) {
                message.success('Plan archived successfully');
                onRefresh();
                fetchPlanDetails(planId);
            } else {
                message.error(response.message || 'Failed to archive plan');
            }
        } catch (error) {
            message.error('Failed to archive plan');
        }
    };

    const handleFeatureToggle = async (featureId, isEnabled) => {
        if (!planDetails || updatingFeatureId !== null) return;

        const previousFeatures = (planDetails.features || []).map((feature) => ({ ...feature }));
        const nextFeatures = previousFeatures.map((feature) => ({
            ...feature,
            IsEnabled: feature.FeatureId === featureId ? isEnabled : !!feature.IsEnabled
        }));

        // Optimistic UI update for immediate feedback.
        setPlanDetails((prev) => prev ? { ...prev, features: nextFeatures } : prev);
        setUpdatingFeatureId(featureId);

        try {
            const response = await execute(
                'PUT',
                `${protectedResources.api.baseEndPoint}/ai-subscription/plans/${selectedPlan.PlanId}/features`,
                {
                    features: nextFeatures.map((feature) => ({
                        featureId: feature.FeatureId,
                        isEnabled: !!feature.IsEnabled
                    }))
                }
            );

            if (!response.success) {
                throw new Error(response.message || 'Failed to update feature');
            }

            message.success('Feature updated');
        } catch (error) {
            // Revert to previous state on failure.
            setPlanDetails((prev) => prev ? { ...prev, features: previousFeatures } : prev);
            message.error('Failed to update feature');
        } finally {
            setUpdatingFeatureId(null);
        }
    };

    const handleSavePolicy = async () => {
        try {
            const values = await policyForm.validateFields();
            setSaving(true);

            const response = await execute(
                'PUT',
                `${protectedResources.api.baseEndPoint}/ai-subscription/policy/${policy.PolicyId}`,
                values
            );

            if (response.success) {
                message.success('Policy updated successfully');
                setIsPolicyModalVisible(false);
                onPolicyRefresh();
            } else {
                message.error(response.message || 'Failed to update policy');
            }
        } catch (error) {
            message.error('Failed to update policy');
        } finally {
            setSaving(false);
        }
    };

    const getStatusTag = (status) => {
        const colors = {
            Draft: 'default',
            Published: 'green',
            Archived: 'red'
        };
        return <Tag color={colors[status] || 'default'}>{status}</Tag>;
    };

    const historyColumns = [
        { title: 'Version', dataIndex: 'VersionNo', key: 'versionNo' },
        { title: 'Change Type', dataIndex: 'ChangeType', key: 'changeType' },
        {
            title: 'Changed On',
            dataIndex: 'ChangedOn',
            key: 'changedOn',
            render: (date) => new Date(date).toLocaleString()
        },
        { title: 'Reason', dataIndex: 'ChangeReason', key: 'changeReason' }
    ];

    return (
        <div>
            <Row gutter={16}>
                {/* Left Panel - Plan List */}
                <Col span={8}>
                    <SectionCard
                        title="Subscription Plans"
                        extra={
                            <Space>
                                <Button
                                    icon={<SettingOutlined />}
                                    onClick={() => setIsPolicyModalVisible(true)}
                                >
                                    Policy
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleCreatePlan}
                                >
                                    Create Plan
                                </Button>
                            </Space>
                        }
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            {plans.map(plan => (
                                <PlanCard
                                    key={plan.PlanId}
                                    $selected={selectedPlan?.PlanId === plan.PlanId}
                                    onClick={() => setSelectedPlan(plan)}
                                    size="small"
                                >
                                    <div className="plan-header">
                                        <span className="plan-name">{plan.PlanName}</span>
                                        {getStatusTag(plan.Status)}
                                    </div>
                                    <div className="plan-tokens">
                                        Token Limit: {formatTokens(plan.TokenLimitMonthly)}/month
                                    </div>
                                </PlanCard>
                            ))}
                        </Space>
                    </SectionCard>
                </Col>

                {/* Right Panel - Plan Details */}
                <Col span={16}>
                    {selectedPlan && planDetails ? (
                        <>
                            <SectionCard
                                title={
                                    <Space>
                                        <span>{planDetails.PlanName}</span>
                                        {getStatusTag(planDetails.Status)}
                                    </Space>
                                }
                                extra={
                                    <Space>
                                        <Button
                                            icon={<HistoryOutlined />}
                                            onClick={() => fetchVersionHistory(selectedPlan.PlanId)}
                                        >
                                            History
                                        </Button>
                                        <Button
                                            icon={<EditOutlined />}
                                            onClick={() => handleEditPlan(planDetails)}
                                        >
                                            Edit
                                        </Button>
                                        {planDetails.Status === 'Draft' && (
                                            <Popconfirm
                                                title="Publish this plan?"
                                                description="This will make the plan available for assignment."
                                                onConfirm={() => handlePublishPlan(selectedPlan.PlanId)}
                                            >
                                                <Button type="primary" icon={<CheckCircleOutlined />}>
                                                    Publish
                                                </Button>
                                            </Popconfirm>
                                        )}
                                        {planDetails.Status === 'Published' && (
                                            <Popconfirm
                                                title="Archive this plan?"
                                                description="This will prevent new assignments."
                                                onConfirm={() => handleArchivePlan(selectedPlan.PlanId)}
                                            >
                                                <Button danger icon={<StopOutlined />}>
                                                    Archive
                                                </Button>
                                            </Popconfirm>
                                        )}
                                    </Space>
                                }
                            >
                                <Row gutter={24}>
                                    <Col span={8}>
                                        <div style={{ color: '#666', fontSize: 12 }}>Plan Code</div>
                                        <div style={{ fontSize: 16, fontWeight: 500 }}>{planDetails.PlanCode}</div>
                                    </Col>
                                    <Col span={8}>
                                        <div style={{ color: '#666', fontSize: 12 }}>Monthly Token Limit</div>
                                        <div style={{ fontSize: 16, fontWeight: 500 }}>
                                            {formatTokens(planDetails.TokenLimitMonthly)}
                                        </div>
                                    </Col>
                                    <Col span={8}>
                                        <div style={{ color: '#666', fontSize: 12 }}>Default User Limit</div>
                                        <div style={{ fontSize: 16, fontWeight: 500 }}>
                                            {formatTokens(planDetails.DefaultUserTokenLimitMonthly)}
                                        </div>
                                    </Col>
                                </Row>
                                {planDetails.Description && (
                                    <div style={{ marginTop: 16, color: '#666' }}>
                                        {planDetails.Description}
                                    </div>
                                )}
                            </SectionCard>

                            <SectionCard title="Feature Matrix">
                                {planDetails.features?.length > 0 ? (
                                    planDetails.features.map(feature => (
                                        <FeatureRow key={feature.FeatureId}>
                                            <div>
                                                <div className="feature-name">{feature.FeatureName}</div>
                                                <div className="feature-area">{feature.Area}</div>
                                            </div>
                                            <Switch
                                                checked={feature.IsEnabled}
                                                loading={updatingFeatureId === feature.FeatureId}
                                                disabled={updatingFeatureId !== null}
                                                onChange={(checked) => handleFeatureToggle(feature.FeatureId, checked)}
                                            />
                                        </FeatureRow>
                                    ))
                                ) : (
                                    <div style={{ textAlign: 'center', color: '#999', padding: 16 }}>
                                        No features available in catalog
                                    </div>
                                )}
                            </SectionCard>

                            <SectionCard
                                title="Pricing Tiers"
                                extra={
                                    <Button icon={<EditOutlined />} onClick={handleEditPricingTiers}>
                                        Edit Pricing
                                    </Button>
                                }
                            >
                                <Table
                                    dataSource={planDetails.pricingTiers}
                                    rowKey="PricingTierId"
                                    pagination={false}
                                    size="small"
                                    columns={[
                                        { title: 'Tier', dataIndex: 'TierName', key: 'tierName' },
                                        { title: 'Min Users', dataIndex: 'MinUsers', key: 'minUsers' },
                                        {
                                            title: 'Max Users',
                                            dataIndex: 'MaxUsers',
                                            key: 'maxUsers',
                                            render: (val) => val || 'Unlimited'
                                        },
                                        {
                                            title: 'Price/User/Month',
                                            dataIndex: 'PricePerUserMonthly',
                                            key: 'price',
                                            render: (val) => `$${val?.toFixed(2)}`
                                        }
                                    ]}
                                />
                            </SectionCard>
                        </>
                    ) : (
                        <SectionCard>
                            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                                Select a plan from the left panel to view details
                            </div>
                        </SectionCard>
                    )}
                </Col>
            </Row>

            {/* Create/Edit Plan Modal */}
            <Modal
                title={modalMode === 'create' ? 'Create New Plan' : 'Edit Plan'}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={handleSavePlan}
                confirmLoading={saving}
                width={600}
            >
                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="planCode"
                                label="Plan Code"
                                rules={[{ required: true, message: 'Please enter plan code' }]}
                            >
                                <Input
                                    placeholder="e.g., GOLD"
                                    disabled={modalMode === 'edit'}
                                    style={{ textTransform: 'uppercase' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="planName"
                                label="Plan Name"
                                rules={[{ required: true, message: 'Please enter plan name' }]}
                            >
                                <Input placeholder="e.g., Gold Plan" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="description" label="Description">
                        <Input.TextArea rows={3} placeholder="Plan description..." />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="tokenLimitMonthly"
                                label="Monthly Token Limit"
                                rules={[{ required: true, message: 'Please enter token limit' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="defaultUserTokenLimitMonthly"
                                label="Default User Token Limit"
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            {/* Pricing Tiers Modal */}
            <Modal
                title={`Edit Pricing Tiers${planDetails?.PlanName ? ` - ${planDetails.PlanName}` : ''}`}
                open={isPricingModalVisible}
                onCancel={() => setIsPricingModalVisible(false)}
                onOk={handleSavePricingTiers}
                confirmLoading={saving}
                width={900}
            >
                <Form form={pricingForm} layout="vertical">
                    <Form.List name="tiers">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field, idx) => (
                                    <Card key={field.key} size="small" style={{ marginBottom: 12 }}>
                                        <Row gutter={12}>
                                            <Col span={5}>
                                                <Form.Item
                                                    name={[field.name, 'tierName']}
                                                    label="Tier Name"
                                                    rules={[{ required: true, message: 'Required' }]}
                                                >
                                                    <Input placeholder="e.g., Small" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={4}>
                                                <Form.Item
                                                    name={[field.name, 'minUsers']}
                                                    label="Min Users"
                                                    rules={[{ required: true, message: 'Required' }]}
                                                >
                                                    <InputNumber min={1} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={4}>
                                                <Form.Item
                                                    name={[field.name, 'maxUsers']}
                                                    label="Max Users"
                                                >
                                                    <InputNumber min={1} style={{ width: '100%' }} placeholder="Unlimited" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={5}>
                                                <Form.Item
                                                    name={[field.name, 'pricePerUserMonthly']}
                                                    label="Price/User/Month"
                                                    rules={[{ required: true, message: 'Required' }]}
                                                >
                                                    <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={4}>
                                                <Form.Item
                                                    name={[field.name, 'pricePerUserAnnual']}
                                                    label="Price/User/Year"
                                                >
                                                    <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={2} style={{ display: 'flex', alignItems: 'end' }}>
                                                <Button
                                                    danger
                                                    onClick={() => remove(field.name)}
                                                    disabled={fields.length === 1}
                                                    style={{ marginBottom: 24 }}
                                                >
                                                    Remove
                                                </Button>
                                            </Col>
                                        </Row>
                                        <Form.Item name={[field.name, 'basePrice']} label="Base Price (Optional)">
                                            <InputNumber min={0} step={0.01} precision={2} style={{ width: 200 }} />
                                        </Form.Item>
                                    </Card>
                                ))}
                                <Button
                                    type="dashed"
                                    icon={<PlusOutlined />}
                                    onClick={() => add({
                                        tierName: '',
                                        minUsers: 1,
                                        maxUsers: null,
                                        pricePerUserMonthly: 0,
                                        pricePerUserAnnual: null,
                                        basePrice: null,
                                        displayOrder: fields.length + 1
                                    })}
                                    block
                                >
                                    Add Pricing Tier
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Form>
            </Modal>

            {/* Policy Configuration Modal */}
            <Modal
                title="Subscription Policy Configuration"
                open={isPolicyModalVisible}
                onCancel={() => setIsPolicyModalVisible(false)}
                onOk={handleSavePolicy}
                confirmLoading={saving}
                width={600}
            >
                <Form form={policyForm} layout="vertical">
                    <Divider orientation="left">Trial Settings</Divider>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="trialDaysDefault" label="Default Trial Days">
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="trialDaysMin" label="Min Trial Days">
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="trialDaysMax" label="Max Trial Days">
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left">Usage Thresholds</Divider>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="warnThresholdPct1" label="Warning 1 (%)">
                                <InputNumber min={0} max={100} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="warnThresholdPct2" label="Warning 2 (%)">
                                <InputNumber min={0} max={100} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="blockThresholdPct" label="Block (%)">
                                <InputNumber min={0} max={100} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="blockAtThreshold" valuePropName="checked">
                        <Switch checkedChildren="Block at threshold" unCheckedChildren="Warn only" />
                    </Form.Item>

                    <Divider orientation="left">Notifications</Divider>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="emailNotificationsEnabled" valuePropName="checked">
                                <Switch checkedChildren="Email Enabled" unCheckedChildren="Email Disabled" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="teamsNotificationsEnabled" valuePropName="checked">
                                <Switch checkedChildren="Teams Enabled" unCheckedChildren="Teams Disabled" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            {/* Version History Modal */}
            <Modal
                title="Plan Version History"
                open={isHistoryModalVisible}
                onCancel={() => setIsHistoryModalVisible(false)}
                footer={null}
                width={800}
            >
                <Table
                    dataSource={versionHistory}
                    columns={historyColumns}
                    rowKey="VersionHistoryId"
                    pagination={false}
                />
            </Modal>
        </div>
    );
};

export default PlanConfiguration;
